import { Loader2, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { GlassCard } from '@/design-system';
import type { PluginMarketplaceSeed } from '@/versions/v1/shared/settings-seed';

type Props = {
  onAdd: (marketplace: PluginMarketplaceSeed) => void;
};

export function AddMarketplaceForm({ onAdd }: Props) {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState('');
  const [source, setSource] = useState<'github' | 'url'>('github');
  const [url, setUrl] = useState('');
  const [ref, setRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmedId = id.trim();
    const trimmedUrl = url.trim();
    if (!trimmedId || !trimmedUrl) return;

    setSubmitting(true);
    try {
      onAdd({
        id: trimmedId,
        name: trimmedId,
        source,
        url: trimmedUrl,
      });
      setId('');
      setUrl('');
      setRef('');
      setOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add marketplace';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
      >
        <Plus className="h-4 w-4" />
        Add marketplace
      </button>
    );
  }

  return (
    <GlassCard className="p-5">
      <form onSubmit={onSubmit} className="grid gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Marketplace id</label>
          <p className="text-xs text-muted">
            Short identifier, e.g. <code>my-org</code>. Letters, digits, hyphens,
            underscores.
          </p>
          <input
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="my-org"
            required
            spellCheck={false}
            autoComplete="off"
            className="w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Source</label>
          <div className="flex gap-2">
            {(['github', 'url'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setSource(t)}
                className={
                  source === t
                    ? 'rounded-full border border-zinc-900 bg-zinc-900 px-3 py-1 text-xs font-medium uppercase tracking-wide text-white dark:border-white dark:bg-white dark:text-zinc-900'
                    : 'rounded-full border border-zinc-200 px-3 py-1 text-xs font-medium uppercase tracking-wide text-zinc-600 transition-colors hover:border-zinc-400 dark:border-zinc-800 dark:text-zinc-400'
                }
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">URL</label>
          <p className="text-xs text-muted">
            {source === 'github'
              ? 'GitHub repo, e.g. https://github.com/owner/repo'
              : 'Direct https URL to a marketplace.json file'}
          </p>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            type="url"
            placeholder={
              source === 'github'
                ? 'https://github.com/owner/repo'
                : 'https://example.com/.claude-plugin/marketplace.json'
            }
            required
            spellCheck={false}
            autoComplete="off"
            className="w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
          />
        </div>

        {source === 'github' ? (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Branch or tag <span className="text-zinc-400">(optional)</span>
            </label>
            <p className="text-xs text-muted">
              Defaults to <code>main</code>.
            </p>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="main"
              spellCheck={false}
              autoComplete="off"
              className="w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
            />
          </div>
        ) : null}

        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setError(null);
            }}
            disabled={submitting}
            className="text-sm text-zinc-500 hover:text-zinc-900 disabled:opacity-50 dark:hover:text-zinc-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-200"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Add marketplace
          </button>
        </div>
      </form>
    </GlassCard>
  );
}
